'use client';

import { useId, useState } from 'react';
import type { CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiBookmark3Line,
  RiMegaphoneLine,
  RiPaletteLine,
  RiShieldCheckLine,
  RiVolumeUpLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { dur, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './creative.css';

/**
 * "Markanız her karede" — marka kiti, AI notları, kalite yargıcı, ses; altında
 * sohbetteki hızlı akışlar.
 *
 * Olgular KREATIF-STUDYO-PLANI.md'den: marka kiti (ton, renk, yazı tipi,
 * logo, son kart, yap/yapma) + AI'ın öğrendiği notlar (görülür, silinir,
 * sabitlenir); kalite yargıcı her çekimi ürün fotoğrafıyla karşılaştırıyor ve
 * eşiği geçemeyen çekim siz görmeden yeniden çekiliyor; TR/EN seslendirme,
 * müzik, altyazı, son kart.
 *
 * Hızlı akışların adları ve açıklamaları panelin KENDİ metinlerinden
 * (`dashboard.agentChat.flows`) okunuyor — sitede başka, panelde başka ad
 * olmasın. Liste `@mentorivo/shared/agent-flows` → AGENT_FLOW_DEFS'in
 * görünür grupları (otomasyon grubu 5 Eki'de kalktı, burada da yok).
 *
 * Hareket: grup değişince akış listesi yeniden diziliyor — satırlar kısa
 * kademeyle bulanıktan netleşiyor; grup düğmesinin seçili zemini `layoutId`
 * ile kayıyor.
 */

const FACTS = [
  { key: 'kit', Icon: RiPaletteLine },
  { key: 'notes', Icon: RiBookmark3Line },
  { key: 'qa', Icon: RiShieldCheckLine },
  { key: 'sound', Icon: RiVolumeUpLine },
] as const;

const GROUPS = {
  image: ['image-product', 'image-lifestyle', 'image-model', 'image-promo', 'image-set', 'image-edit'],
  video: ['video-ad', 'video-clip', 'video-ugc', 'video-template'],
  brand: ['brand-mockups', 'brand-kit', 'brand-logo-find'],
  campaign: ['campaign-new', 'campaign-optimize', 'campaign-budget'],
} as const;
type Group = keyof typeof GROUPS;
const GROUP_KEYS = Object.keys(GROUPS) as Group[];

const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
  exit: {},
};

const rowV: Variants = {
  hidden: { opacity: 0, y: 6, filter: 'blur(3px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  exit: { opacity: 0, transition: { duration: dur.instant } },
};

export function CreativeBrand() {
  const t = useTranslations('featuresCreative.brand');
  const tf = useTranslations('dashboard.agentChat.flows');
  const uid = useId();
  const [group, setGroup] = useState<Group>('brand');

  return (
    <ProductSection tone="white" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <ul className="ps-facts" style={{ '--ps-facts': 4 } as CSSProperties}>
        {FACTS.map(({ key, Icon }) => (
          <li key={key} className="ps-fact">
            <Icon className="ps-fact-icon" size={20} aria-hidden="true" />
            <p className="ps-fact-title">{t(`facts.${key}.title`)}</p>
            <p className="ps-fact-text">{t(`facts.${key}.text`)}</p>
          </li>
        ))}
      </ul>

      <div className="crb">
        <div className="crb-copy">
          <p className="crb-title">
            <RiMegaphoneLine size={18} aria-hidden="true" />
            {t('chat.title')}
          </p>
          <p className="crb-text">{t('chat.text')}</p>
        </div>

        <div className="crb-composer">
          <p className="crb-input">{t('chat.placeholder')}</p>
          <div className="crb-groups" role="group" aria-label={t('chat.groupsLabel')}>
            {GROUP_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className="crb-group"
                aria-pressed={group === key}
                aria-controls={`${uid}-flows`}
                onClick={() => setGroup(key)}
              >
                {group === key && (
                  <motion.span
                    layoutId={`${uid}-group`}
                    className="crb-group-pill"
                    transition={spring.snap}
                    aria-hidden="true"
                  />
                )}
                <span className="crb-group-label">{tf(`group.${key}`)}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait" initial={false}>
            <motion.ul
              key={group}
              className="crb-flows"
              id={`${uid}-flows`}
              aria-live="polite"
              variants={listV}
              initial="hidden"
              animate="show"
              exit="exit"
            >
              {GROUPS[group].map((flow) => (
                <motion.li key={flow} className="crb-flow" variants={rowV}>
                  <p className="crb-flow-name">{tf(`flow.${flow}.name`)}</p>
                  <p className="crb-flow-hint">{tf(`flow.${flow}.hint`)}</p>
                </motion.li>
              ))}
            </motion.ul>
          </AnimatePresence>

          <p className="crb-foot">{t('chat.foot')}</p>
        </div>
      </div>
    </ProductSection>
  );
}
